import { ReactNode, useCallback, useRef } from 'react'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import classNames from 'classnames'
import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react'

import { InfoBadge } from '@/components/InfoBadge'

import { Menu } from './Menu'
import { ColorVariant, DropdownArrow } from './types'

type DropdownProps = {
    className: string
    activeClassName: string
    popupContent?: (close: () => void) => ReactNode
    noPopupFocus?: boolean
    menuItems?: ReactNode
    onTogglePopover?: (open: boolean) => void
    dropdownArrow?: DropdownArrow
    children: ReactNode
}

export const Dropdown = ({
    className: sourceClassName,
    activeClassName,
    popupContent,
    noPopupFocus,
    menuItems,
    onTogglePopover,
    dropdownArrow = 'default',
    children,
}: DropdownProps) => {
    const className = classNames(sourceClassName, {
        'dropdown-arrow': dropdownArrow !== 'none',
        '[&:not(:hover)]:bg-none': dropdownArrow === 'hover',
    })

    const previousOpen = useRef(false)

    const updateOpen = useCallback(
        (open: boolean) => {
            if (open !== previousOpen.current) {
                previousOpen.current = open

                if (onTogglePopover) {
                    // call with timeout to get it out of rendering cylce
                    setTimeout(() => onTogglePopover(open), 0)
                }
            }
        },
        [onTogglePopover]
    )

    if (popupContent) {
        return (
            <Popover className="relative leading-none">
                {({ open, close }) => {
                    updateOpen(open)

                    return (
                        <>
                            <PopoverButton
                                className={classNames('focus-outline cursor-default', className, {
                                    [activeClassName]: open,
                                })}
                            >
                                {children}
                            </PopoverButton>
                            <PopoverBackdrop className="fixed inset-0 z-10 bg-black/0" />
                            <PopoverPanel className="popup z-20" anchor="bottom start" portal focus={!noPopupFocus}>
                                {open && popupContent(close)}
                            </PopoverPanel>
                        </>
                    )
                }}
            </Popover>
        )
    } else {
        return (
            <Menu as="div" className="leading-none">
                {({ open }) => {
                    updateOpen(open)

                    return (
                        <>
                            <Menu.Button
                                className={classNames(className, {
                                    [activeClassName]: open,
                                })}
                            >
                                {children}
                            </Menu.Button>
                            {open && <Menu.Items portal>{menuItems}</Menu.Items>}
                        </>
                    )
                }}
            </Menu>
        )
    }
}

type PickerProps = Omit<DropdownProps, 'dropdownArrow' | 'className' | 'activeClassName'> & {
    className?: string
    secondary?: boolean
}

Dropdown.Picker = ({ children, className, secondary, ...dropdownProps }: PickerProps) => (
    <Dropdown
        {...dropdownProps}
        className={classNames(className, 'focus-outline inline-flex cursor-pointer items-center', {
            'text-link': !secondary,
            'text-link-secondary': secondary,
        })}
        activeClassName="active"
        dropdownArrow="none"
    >
        <div>{children}</div>
        <ChevronDownIcon className="size-5 shrink-0" />
    </Dropdown>
)

type BoxedProps = Omit<DropdownProps, 'dropdownArrow' | 'className' | 'activeClassName'> & {
    className?: string
    color?: ColorVariant
    error?: string
}

Dropdown.Boxed = ({ children, className, color: sourceColor, error, ...dropdownProps }: BoxedProps) => {
    const color = error ? 'red' : sourceColor

    return (
        <Dropdown
            {...dropdownProps}
            className={classNames(
                className,
                'focus-outline flex cursor-pointer items-center rounded py-1 pl-2 pr-1 font-medium',
                {
                    'text-secondary hover:text-primary ring-1 ring-neutral-300 dark:ring-neutral-600': !color,
                    'bg-neutral-200 text-neutral-800 hover:bg-neutral-300 active:bg-neutral-400 dark:bg-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-600 dark:active:bg-neutral-500':
                        color === 'neutral',
                    'bg-green-100 text-green-800 hover:bg-green-200 active:bg-green-300 dark:bg-green-900 dark:text-green-100 dark:hover:bg-green-800 dark:active:bg-green-700':
                        color === 'green',
                    'bg-red-100 text-red-800 hover:bg-red-200 active:bg-red-300 dark:bg-red-900 dark:text-red-100 dark:hover:bg-red-800 dark:active:bg-red-700':
                        color === 'red',
                    'bg-orange-100 text-orange-800 hover:bg-orange-200 active:bg-orange-300 dark:bg-orange-900 dark:text-orange-100 dark:hover:bg-orange-800 dark:active:bg-orange-700':
                        color === 'orange',
                    'bg-blue-100 text-blue-800 hover:bg-blue-200 active:bg-blue-300 dark:bg-blue-900 dark:text-blue-100 dark:hover:bg-blue-800 dark:active:bg-blue-700':
                        color === 'blue',
                    'bg-purple-100 text-purple-800 hover:bg-purple-200 active:bg-purple-300 dark:bg-purple-900 dark:text-purple-100 dark:hover:bg-purple-800 dark:active:bg-purple-700':
                        color === 'purple',
                }
            )}
            activeClassName={classNames({
                'text-primary': !color,
                'bg-neutral-400 dark:bg-neutral-500': color === 'neutral',
                'bg-green-300 dark:bg-green-700': color === 'green',
                'bg-red-300 dark:bg-red-700': color === 'red',
                'bg-orange-300 dark:bg-orange-700': color === 'orange',
                'bg-blue-300 dark:bg-blue-700': color === 'blue',
                'bg-purple-300 dark:bg-purple-700': color === 'purple',
            })}
            dropdownArrow="none"
        >
            <div>{children}</div>
            {error && (
                <InfoBadge className="ml-1" small error>
                    {error}
                </InfoBadge>
            )}
            <ChevronDownIcon className="size-5 shrink-0" />
        </Dropdown>
    )
}
